import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import api from "../api/api";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Grid,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

const BLOOD_GROUP_OPTIONS = [
  "A+",
  "A-",
  "A1+",
  "A1-",
  "A2+",
  "A2-",
  "B+",
  "B-",
  "AB+",
  "AB-",
  "A1B+",
  "A1B-",
  "A2B+",
  "A2B-",
  "O+",
  "O-",
  "Bombay Blood Group",
  "Rh-null",
];
const PROGRAM_TYPE_OPTIONS = [
  { label: "UG", value: "UG", apiValue: "UG" },
  { label: "PG", value: "PG", apiValue: "PG" },
];

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - 2008 + 1 },
  (_, index) => String(CURRENT_YEAR - index),
);

const getErrorMessage = (errorData, fallbackMessage) => {
  if (!errorData) return fallbackMessage;

  if (typeof errorData === "string") return errorData;
  if (typeof errorData.message === "string") return errorData.message;
  if (typeof errorData.error === "string") return errorData.error;

  if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
    const firstError = errorData.errors[0];
    if (typeof firstError === "string") return firstError;
    if (typeof firstError?.message === "string") return firstError.message;
  }

  if (errorData.errors && typeof errorData.errors === "object") {
    const firstFieldError = Object.values(errorData.errors).find((value) => {
      if (typeof value === "string" && value.trim()) return true;
      if (Array.isArray(value) && value.length > 0) return true;
      return false;
    });

    if (typeof firstFieldError === "string") return firstFieldError;
    if (Array.isArray(firstFieldError) && firstFieldError.length > 0) {
      return firstFieldError[0];
    }
  }

  return fallbackMessage;
};

const getBackendFieldErrors = (errorData) => {
  if (!errorData?.errors || typeof errorData.errors !== "object") {
    return {};
  }

  return Object.entries(errorData.errors).reduce((acc, [key, value]) => {
    if (typeof value === "string" && value.trim()) {
      acc[key] = value;
    } else if (Array.isArray(value) && value.length > 0) {
      acc[key] = value[0];
    }

    return acc;
  }, {});
};

function Register() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [departments, setDepartments] = useState([]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    email: "",
    password: "",
    confirmPassword: "",
    programType: "UG",
    yearOfPassedOut: "",
    department: "",
    currentJob: "",
    companyName: "",
    bloodGroup: "",
    interestToDonateBlood: "",
    careerGuidanceVolunteer: "No",
    volunteerAlumniActivities: "",
    city: "",
    state: "",
    country: "",
  });

  useEffect(() => {
    const loadDepartments = async () => {
      try {
        const res = await api.getDepartments();
        const data = res?.data || res;
        setDepartments(data?.departments || []);
      } catch (err) {
        const msg = getErrorMessage(
          err?.response?.data,
          err.message || "Unable to load departments.",
        );
        setError(msg);
      }
    };

    loadDepartments();
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
      ...(name === "programType" ? { department: "" } : {}),
    }));
    setForm((prev) => ({
      ...prev,
      [name]: value,
      ...(name === "programType" ? { department: "" } : {}),
    }));
  };

  const selectedProgramType = PROGRAM_TYPE_OPTIONS.find(
    (option) => option.value === form.programType,
  );
  const availableDepartments = departments.filter(
    (department) => department?.ProgramType === selectedProgramType?.apiValue,
  );

  const validateForm = () => {
    const errors = {};

    if (!form.firstName.trim()) errors.firstName = "First name is required.";
    if (!form.lastName.trim()) errors.lastName = "Last name is required.";
    if (!form.mobileNumber.trim()) {
      errors.mobileNumber = "Mobile number is required.";
    }
    if (!form.email.trim()) errors.email = "Email is required.";
    if (!form.password) errors.password = "Password is required.";
    if (!form.confirmPassword) {
      errors.confirmPassword = "Confirm password is required.";
    } else if (form.password !== form.confirmPassword) {
      errors.confirmPassword = "Password and confirm password must match.";
    }
    if (!form.programType) errors.programType = "Program type is required.";
    if (!form.yearOfPassedOut) {
      errors.yearOfPassedOut = "Year of passed out is required.";
    }
    if (!form.department) errors.department = "Department is required.";
    if (form.interestToDonateBlood === "Yes" && !form.bloodGroup) {
      errors.bloodGroup =
        "Blood group is required when interested to donate blood.";
    }
    if (!form.city.trim()) errors.city = "City is required.";
    if (!form.state.trim()) errors.state = "State is required.";
    if (!form.country.trim()) errors.country = "Country is required.";

    return errors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    const errors = validateForm();
    setFieldErrors(errors);

    if (Object.keys(errors).length > 0) {
      setError("Please fix the highlighted fields.");
      return;
    }

    setLoading(true);

    try {
      // Build payload matching backend requirements
      const payload = {
        Email: form.email,
        PasswordHash: form.password,
        FirstName: form.firstName,
        LastName: form.lastName,
        MobileNumber: form.mobileNumber,
        ProgramType: form.programType,
        YearOfPassedOut: form.yearOfPassedOut
          ? Number(form.yearOfPassedOut)
          : null,
        Department: form.department,
        CurrentJob: form.currentJob,
        CompanyName: form.companyName,
        BloodGroup: form.bloodGroup,
        IntrestToDonateBlood: form.interestToDonateBlood === "Yes" ? 1 : 0,
        CareerGuidanceVolunteer: form.careerGuidanceVolunteer === "Yes" ? 1 : 0,
        VolunteerAlumniActivities:
          form.volunteerAlumniActivities === "Yes" ? 1 : 0,
        City: form.city,
        State: form.state,
        Country: form.country,
        Role: "user",
      };

      const res = await api.registerUser(payload);
      const data = res?.data || res;
      setSuccessMessage(data?.message || "Registration successful.");
      setSuccess(true);
    } catch (err) {
      const errorData = err?.response?.data;
      const backendFieldErrors = getBackendFieldErrors(errorData);
      if (Object.keys(backendFieldErrors).length > 0) {
        setFieldErrors((prev) => ({ ...prev, ...backendFieldErrors }));
      }

      const msg = getErrorMessage(
        errorData,
        err.message || "Something went wrong while creating your account.",
      );
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "linear-gradient(135deg, #f4f9ff 0%, #eef5ff 100%)",
          px: 3,
          py: 4,
        }}
      >
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{ p: { xs: 4, sm: 6 }, borderRadius: 4, textAlign: "center" }}
          >
            <Typography
              variant="h4"
              fontWeight={700}
              sx={{ mb: 1, color: "#0f172a" }}
            >
              Thank you for registering
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {successMessage ||
                "Your account has been submitted successfully. An administrator will review your request and approve your access shortly."}
            </Typography>
            <Alert severity="info" sx={{ mb: 3, justifyContent: "center" }}>
              Once approved, you will be able to sign in and access the portal.
            </Alert>
            <Button variant="contained" onClick={() => navigate("/login")}>
              Back to login
            </Button>
          </Paper>
        </Container>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "linear-gradient(135deg, #f4f9ff 0%, #eef5ff 100%)",
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 16px 45px rgba(15, 23, 42, 0.12)",
          }}
        >
          <Box sx={{ p: { xs: 3, sm: 4 }, bgcolor: "#0f172a", color: "white" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
              <Box
                component="img"
                src="/images/seeds_logo.png"
                alt="Einstein Seeds logo"
                sx={{
                  width: 68,
                  height: 68,
                  borderRadius: 3,
                  objectFit: "contain",
                  bgcolor: "white",
                  p: 1,
                }}
              />
              <Box>
                <Typography variant="h4" fontWeight={700}>
                  Create your account
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ mt: 0.5, color: "rgba(255,255,255,0.82)" }}
                >
                  Join Einstein Seeds and complete your alumni profile.
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            ) : null}
            <Stack component="form" spacing={3} onSubmit={handleSubmit}>
              <Typography variant="h6" fontWeight={600}>
                Personal details
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="First name"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.firstName)}
                    helperText={fieldErrors.firstName}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Last name"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.lastName)}
                    helperText={fieldErrors.lastName}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Mobile number"
                    name="mobileNumber"
                    value={form.mobileNumber}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.mobileNumber)}
                    helperText={fieldErrors.mobileNumber}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.email)}
                    helperText={fieldErrors.email}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Password"
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.password)}
                    helperText={fieldErrors.password}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Confirm password"
                    name="confirmPassword"
                    type="password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.confirmPassword)}
                    helperText={fieldErrors.confirmPassword}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Year of passed out"
                    name="yearOfPassedOut"
                    select
                    value={form.yearOfPassedOut}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.yearOfPassedOut)}
                    helperText={fieldErrors.yearOfPassedOut}
                    required
                  >
                    {YEAR_OPTIONS.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Program type"
                    name="programType"
                    select
                    value={form.programType}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.programType)}
                    helperText={fieldErrors.programType}
                    required
                  >
                    {PROGRAM_TYPE_OPTIONS.map((programType) => (
                      <MenuItem
                        key={programType.value}
                        value={programType.value}
                      >
                        {programType.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Department"
                    name="department"
                    select
                    value={form.department}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.department)}
                    helperText={fieldErrors.department}
                    required
                    disabled={availableDepartments.length === 0}
                  >
                    {availableDepartments.map((department) => (
                      <MenuItem
                        key={department.DepartmentId}
                        value={department.DepartmentName}
                      >
                        {department.DepartmentName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Divider />
              <Typography variant="h6" fontWeight={600}>
                Professional & community details
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Current job"
                    name="currentJob"
                    value={form.currentJob}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Company name"
                    name="companyName"
                    value={form.companyName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Blood group"
                    name="bloodGroup"
                    select
                    value={form.bloodGroup}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.bloodGroup)}
                    helperText={fieldErrors.bloodGroup}
                  >
                    {BLOOD_GROUP_OPTIONS.map((bloodGroup) => (
                      <MenuItem key={bloodGroup} value={bloodGroup}>
                        {bloodGroup}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Interested to donate blood"
                    name="interestToDonateBlood"
                    select
                    value={form.interestToDonateBlood}
                    onChange={handleChange}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Career guidance volunteer"
                    name="careerGuidanceVolunteer"
                    select
                    value={form.careerGuidanceVolunteer}
                    onChange={handleChange}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    label="Volunteer alumni activities"
                    name="volunteerAlumniActivities"
                    select
                    value={form.volunteerAlumniActivities}
                    onChange={handleChange}
                  >
                    <MenuItem value="Yes">Yes</MenuItem>
                    <MenuItem value="No">No</MenuItem>
                  </TextField>
                </Grid>
              </Grid>

              <Divider />
              <Typography variant="h6" fontWeight={600}>
                Location details
              </Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="City"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.city)}
                    helperText={fieldErrors.city}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="State"
                    name="state"
                    value={form.state}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.state)}
                    helperText={fieldErrors.state}
                    required
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    fullWidth
                    label="Country"
                    name="country"
                    value={form.country}
                    onChange={handleChange}
                    error={Boolean(fieldErrors.country)}
                    helperText={fieldErrors.country}
                    required
                  />
                </Grid>
              </Grid>

              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  alignSelf: "flex-start",
                  px: 3,
                  py: 1.2,
                  borderRadius: 2,
                }}
              >
                {loading ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={18} color="inherit" />
                    Creating account...
                  </Box>
                ) : (
                  "Register account"
                )}
              </Button>

              <Typography variant="body2" color="text.secondary">
                Already have an account?{" "}
                <Link component={RouterLink} to="/login" underline="hover">
                  Sign in
                </Link>
              </Typography>
            </Stack>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Register;
